// Campus campus-web features/ai/lib 에서 옮겨 왔다(PICNIC-MB-FIELD-001 §6 3단계 — 놀이터 진짜 카메라). 고칠 때는 두 곳을 함께.
/**
 * 온디바이스 분류기(피지컬 AI · PAI-01·03·04) — <b>최소 버전 v1</b>.
 *
 * <p><b>무전송(P-PA2).</b> 이미지 원본은 이 파일 밖으로 나가지 않는다 — 프레임을 <b>작은 특징 벡터</b>(다운스케일
 * 그레이스케일 숫자)로 줄여 그것만 다룬다. 서버로 가는 것은 모델 <b>메타</b>(클래스·샘플 수)뿐이다(PAI-08).
 *
 * <p><b>기술.</b> v1 은 의존성 없는 <b>최근접-중심(nearest-centroid)</b>이다 — 클래스마다 특징 벡터의 평균을
 * 쌓고(즉시 '학습'), 지금 프레임을 가장 가까운 중심의 클래스로 분류한다. 정확도는 낮지만 즉각적이고(≤수ms)
 * 브라우저에서 바로 돈다. 정확도가 필요하면 <b>같은 인터페이스 뒤로</b> tf.js MobileNet+KNN 으로 바꾼다(PAI-04).
 *
 * <p><b>정확도는 모델의 성적이지 학생의 성적이 아니다</b>(P-PA6) — 신뢰도 수치는 실험 도구일 뿐, 화면 표현은 SE/SU 가 나눈다.
 */

/** 프레임 하나를 특징 벡터로. `grid×grid` 그레이스케일(0~1). 이미지 원본이 아니라 <b>이 숫자만</b> 모델에 들어간다. */
export function extractFeatures(
  img: { data: Uint8ClampedArray | number[]; width: number; height: number },
  grid = 8,
): number[] {
  const f = new Array<number>(grid * grid).fill(0);
  const counts = new Array<number>(grid * grid).fill(0);
  for (let y = 0; y < img.height; y++) {
    const gy = Math.min(grid - 1, Math.floor((y / img.height) * grid));
    for (let x = 0; x < img.width; x++) {
      const gx = Math.min(grid - 1, Math.floor((x / img.width) * grid));
      const i = (y * img.width + x) * 4;
      const lum = (0.299 * img.data[i]! + 0.587 * img.data[i + 1]! + 0.114 * img.data[i + 2]!) / 255;
      const cell = gy * grid + gx;
      f[cell]! += lum;
      counts[cell]! += 1;
    }
  }
  for (let c = 0; c < f.length; c++) if (counts[c]) f[c]! /= counts[c]!;
  return f;
}

export type Prediction = { label: string; confidence: number };

/** 최근접-중심 분류기. 클래스별 특징 평균(중심)만 갖는다 — 원본 프레임은 담지 않는다(P-PA2). */
export class NearestCentroid {
  private readonly sum = new Map<string, number[]>();
  private readonly n = new Map<string, number>();

  /** 이 클래스의 예로 특징 벡터 하나를 담는다(즉시 '학습'). */
  addSample(label: string, features: readonly number[]): void {
    const s = this.sum.get(label);
    if (!s) { this.sum.set(label, [...features]); this.n.set(label, 1); return; }
    for (let i = 0; i < s.length && i < features.length; i++) s[i]! += features[i]!;
    this.n.set(label, (this.n.get(label) ?? 0) + 1);
  }

  /** 이 클래스를 잊는다(삭제) — 담은 샘플을 버린다. */
  forget(label: string): void { this.sum.delete(label); this.n.delete(label); }

  /** 학습 상태를 숫자로 — 저장·복원용(특징 평균과 수). 원본·이미지는 없다(P-PA2). */
  toJSON(): { sum: Record<string, number[]>; n: Record<string, number> } {
    return { sum: Object.fromEntries(this.sum), n: Object.fromEntries(this.n) };
  }
  static fromJSON(o: { sum: Record<string, number[]>; n: Record<string, number> }): NearestCentroid {
    const m = new NearestCentroid();
    for (const [k, v] of Object.entries(o.sum ?? {})) m.sum.set(k, [...v]);
    for (const [k, v] of Object.entries(o.n ?? {})) m.n.set(k, v);
    return m;
  }

  /** 샘플이 있는 클래스들. */
  labels(): string[] { return [...this.n.keys()].filter((l) => (this.n.get(l) ?? 0) > 0); }
  /** 클래스별 담은 샘플 수 — 메타(PAI-08)로 서버에 올라갈 수 있는 유일한 종류의 값. */
  sampleCount(label: string): number { return this.n.get(label) ?? 0; }

  /**
   * 지금 프레임을 분류한다. 학습된 클래스가 없으면 `{label:"", confidence:0}`(모르겠음).
   * 신뢰도는 가장 가까운·둘째 가까운 거리의 비로 낸다(0.5=애매, 1=확실). 임계 미만은 호출부가 '모르겠음' 으로 본다.
   */
  classify(features: readonly number[]): Prediction {
    const labels = this.labels();
    if (labels.length === 0) return { label: "", confidence: 0 };
    const dists = labels.map((l) => ({ l, d: this.distance(l, features) })).sort((a, b) => a.d - b.d);
    const best = dists[0]!;
    if (labels.length === 1) return { label: best.l, confidence: 1 };
    const second = dists[1]!;
    // 두 거리가 비슷하면 애매(≈0.5), 최선이 훨씬 가까우면 확실(→1).
    const conf = second.d + best.d === 0 ? 1 : second.d / (second.d + best.d);
    return { label: best.l, confidence: conf };
  }

  /** 모델 메타(PAI-08) — 클래스·샘플 수만. 원본·특징 벡터는 내보내지 않는다(무전송 계약). */
  meta(): { classes: number; samples: Record<string, number> } {
    const samples: Record<string, number> = {};
    for (const l of this.labels()) samples[l] = this.sampleCount(l);
    return { classes: this.labels().length, samples };
  }

  private distance(label: string, f: readonly number[]): number {
    const s = this.sum.get(label)!;
    const n = this.n.get(label) ?? 1;
    let d = 0;
    for (let i = 0; i < s.length; i++) { const c = s[i]! / n; d += (c - (f[i] ?? 0)) ** 2; }
    return Math.sqrt(d);
  }
}

/** 프레임 입력 — 두 분류기가 각자 방식으로 특징을 뽑는다(Simple 은 다운스케일, tf 는 MobileNet). */
export type Frame = HTMLCanvasElement;

/**
 * 분류기 공통 계약(PAI-03·04) — tf.js MobileNet+KNN 과 무의존 최근접-중심이 <b>같은 얼굴</b>이라
 * 화면은 어느 쪽인지 몰라도 된다. 팩토리가 기기 지원을 보고 고르고, 안 되면 폴백한다.
 */
/** 저장된 모델 — 기기 로컬 보관·복원용. `kind` 가 다르면 특징 공간이 달라 서로 못 읽는다(같은 kind 로만 복원). */
export type SavedModel = { kind: "tf" | "simple"; data: unknown };

export interface Classifier {
  /** 어느 구현인가 — 화면이 '정확 모델/간단 모델' 을 표시하는 데만 쓴다. */
  readonly kind: "tf" | "simple";
  /** 학습 상태를 저장 가능한 형태로(원본·이미지 없음 · P-PA2). 복원은 팩토리 restoreClassifier 가 kind 를 보고 한다. */
  serialize(): SavedModel;
  addSample(label: string, frame: Frame): Promise<void>;
  classify(frame: Frame): Promise<Prediction>;
  forget(label: string): void;
  labels(): string[];
  sampleCount(label: string): number;
  meta(): { classes: number; samples: Record<string, number> };
}

/** 캔버스 → ImageData → 특징. jsdom 등 2D 컨텍스트가 없으면 0 벡터(테스트는 NearestCentroid 로 논리를 본다). */
function featuresOf(frame: Frame, grid = 8): number[] {
  const ctx = frame.getContext("2d");
  if (!ctx) return new Array<number>(grid * grid).fill(0);
  return extractFeatures(ctx.getImageData(0, 0, frame.width, frame.height), grid);
}

/** 무의존 폴백 분류기 — 프레임을 다운스케일 그레이스케일 특징으로 줄여 최근접-중심으로 분류한다. */
export class SimpleClassifier implements Classifier {
  readonly kind = "simple" as const;
  private nc = new NearestCentroid();
  serialize(): SavedModel { return { kind: "simple", data: this.nc.toJSON() }; }
  /** 저장본으로 되살린다 — 같은 kind("simple") 데이터라야 한다. */
  static restore(data: unknown): SimpleClassifier {
    const c = new SimpleClassifier();
    c.nc = NearestCentroid.fromJSON(data as { sum: Record<string, number[]>; n: Record<string, number> });
    return c;
  }
  async addSample(label: string, frame: Frame): Promise<void> { this.nc.addSample(label, featuresOf(frame)); }
  async classify(frame: Frame): Promise<Prediction> { return this.nc.classify(featuresOf(frame)); }
  forget(label: string): void { this.nc.forget(label); }
  labels(): string[] { return this.nc.labels(); }
  sampleCount(label: string): number { return this.nc.sampleCount(label); }
  meta(): { classes: number; samples: Record<string, number> } { return this.nc.meta(); }
}
